import React from "react";
import ReactDOM from "react-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { findCertificatesByCriteria, issueCertificate } from "api/action/certificate";
import { publishAvpsEvent } from "api/action/order";
import { downloadFileFromStore } from "api/action/s3Bucket";
import { formatDateInCompanyTimeZone } from "utils/format";
import CertificateDocument from "./CertificateDocument.jsx";

const trimValue = (value) => String(value || "").trim();

const getTextValue = (...values) => {
  const match = values.find((value) => trimValue(value));
  return trimValue(match);
};

const normalizeStatus = (status) => trimValue(status).replace(/\s+/g, "_").toUpperCase();

const toNumber = (...values) => {
  const match = values.find((value) => value !== undefined && value !== null && trimValue(value) !== "");
  const parsed = Number(match);
  return Number.isFinite(parsed) ? parsed : null;
};

const getGradeFromPercentage = (percentage) => {
  if (percentage === null || percentage === undefined) return "";
  if (percentage >= 100) return "A+";
  if (percentage >= 90) return "A";
  if (percentage >= 80) return "B+";
  if (percentage >= 70) return "B";
  if (percentage >= 60) return "C";
  return "Fail";
};

const getEvaluationPercentage = (evaluation = {}) => {
  const directPercentage = toNumber(
    evaluation?.grade?.percentage,
    evaluation?.percentage,
    evaluation?.scorePercentage,
    evaluation?.assessmentScore,
    evaluation?.finalExamScore,
    evaluation?.score
  );
  if (directPercentage !== null) return directPercentage;

  const obtainedMarks = toNumber(
    evaluation?.grade?.obtainedMarks,
    evaluation?.obtainedMarks,
    evaluation?.marksObtained,
    evaluation?.scoreObtained
  );
  const totalMarks = toNumber(
    evaluation?.grade?.totalMarks,
    evaluation?.totalMarks,
    evaluation?.maxMarks,
    evaluation?.scoreTotal
  );
  if (obtainedMarks !== null && totalMarks > 0) {
    return (obtainedMarks / totalMarks) * 100;
  }

  return null;
};

const isEvaluatedPassingResult = (evaluation = {}) => {
  const status = normalizeStatus(evaluation?.status);
  const gradeLevel = normalizeStatus(evaluation?.grade?.level || evaluation?.gradeLevel);
  return status === "EVALUATED" && evaluation?.grade?.passFlag !== false && gradeLevel !== "FAIL";
};

export const resolveCertificateGrade = ({ evaluationResult, exams } = {}) => {
  const candidates = [
    ...(Array.isArray(evaluationResult) ? evaluationResult : evaluationResult ? [evaluationResult] : []),
    ...(Array.isArray(exams) ? exams : exams ? [exams] : [])
  ].filter(Boolean);

  const evaluatedResults = candidates.filter(isEvaluatedPassingResult);
  const finalExamResult = evaluatedResults.find((result) => (
    normalizeStatus(result?.exam?.subType || result?.subType) === "FINAL"
  ));
  const selectedResult = finalExamResult || evaluatedResults[0];
  if (!selectedResult) return "";

  const gradeLevel = getTextValue(selectedResult?.grade?.level, selectedResult?.gradeLevel);
  if (gradeLevel && normalizeStatus(gradeLevel) !== "FAIL") return gradeLevel;

  const percentage = getEvaluationPercentage(selectedResult);
  return getGradeFromPercentage(percentage);
};

const getIntegerValue = (...values) => {
  const match = values.find((value) => value !== undefined && value !== null && trimValue(value) !== "");
  const parsed = parseInt(match, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value, companyOrCode) => (
  formatDateInCompanyTimeZone(value, companyOrCode, "MMM dd, yyyy, h:mm a", "")
);

const toDatePayload = (value) => {
  const date = value instanceof Date ? value : new Date(value || Date.now());
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
};

const waitForNextPaint = () => new Promise((resolve) => {
  window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
});

const isDataUrl = (value) => /^data:/i.test(String(value || "").trim());

const extractS3ObjectKey = (value) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";

  if (!/^https?:\/\//i.test(normalizedValue)) {
    return normalizedValue.replace(/^\/+/, "");
  }

  try {
    const parsedUrl = new URL(normalizedValue);
    return decodeURIComponent(parsedUrl.pathname.replace(/^\/+/, ""));
  } catch {
    return "";
  }
};

const readBlobAsDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : "");
  reader.onerror = () => reject(reader.error || new Error("Failed to read logo image."));
  reader.readAsDataURL(blob);
});

const logoDataUrlCache = new Map();
const certificateIssueRequests = new Map();
const certificateNotificationRequests = new Map();

const getLogoDataUrl = async (source, dispatch) => {
  const normalizedSource = String(source || "").trim();
  if (!normalizedSource || !dispatch) return "";
  if (isDataUrl(normalizedSource)) return normalizedSource;

  if (!logoDataUrlCache.has(normalizedSource)) {
    logoDataUrlCache.set(
      normalizedSource,
      (async () => {
        try {
          const blob = await dispatch(downloadFileFromStore(normalizedSource));
          return blob ? await readBlobAsDataUrl(blob) : "";
        } catch {
          return "";
        }
      })()
    );
  }

  return logoDataUrlCache.get(normalizedSource);
};

const waitForImageElement = async (img) => {
  if (!img) return;

  if (img.complete) {
    if (img.naturalWidth > 0 && typeof img.decode === "function") {
      try {
        await img.decode();
      } catch {
        // Ignore decode failures for images that already rendered.
      }
    }
    return;
  }

  await new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      resolve();
    }, 8000);

    const done = () => {
      window.clearTimeout(timeoutId);
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      resolve();
    };

    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });
};

const waitForNodeImages = async (node) => {
  const images = Array.from(node?.querySelectorAll?.("img") || []);
  if (!images.length) return;
  await Promise.all(images.map(waitForImageElement));
};

const arrayBufferToBase64 = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";

  for (let index = 0; index < bytes.length; index += 0x8000) {
    const chunk = bytes.subarray(index, index + 0x8000);
    binary += String.fromCharCode(...chunk);
  }

  return window.btoa(binary);
};

const isCertificateFlagEnabled = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  if (typeof value === "number") return value > 0;
  if (typeof value === "string") {
    return ["TRUE", "YES", "Y", "ENABLED", "ACTIVE", "1"].includes(value.trim().toUpperCase());
  }
  if (typeof value === "object") {
    return !value.deleted && value.enabled !== false && value.active !== false;
  }
  return false;
};

export const getCourseProduct = (course = {}) => (
  course?.product?.primaryId ? course.product :
  course?.scheduledProduct?.product?.primaryId ? course.scheduledProduct.product :
  course?.order?.scheduledProduct?.product?.primaryId ? course.order.scheduledProduct.product :
  course?.order?.product?.primaryId ? course.order.product :
  course?.userCurriculum?.product?.primaryId ? course.userCurriculum.product :
  course?.curriculum?.product?.primaryId ? course.curriculum.product :
  course
);

export const hasCertificateEnabled = (course = {}) => {
  const product = getCourseProduct(course);
  return [
    course?.certificateEnabled,
    course?.product?.certificateEnabled,
    product?.certificateEnabled,
    course?.scheduledProduct?.certificateEnabled,
    course?.scheduledProduct?.product?.certificateEnabled,
    course?.order?.scheduledProduct?.product?.certificateEnabled,
    course?.order?.product?.certificateEnabled,
  ].some(isCertificateFlagEnabled);
};

export const getUserName = (user) => (
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim()
  || user?.name
  || user?.email
  || "Learner"
);

export const getCertificateFileName = (certificate) => {
  const identifier = getTextValue(certificate?.identifier, certificate?.primaryId, "certificate");
  return `${identifier}`.replace(/[^a-zA-Z0-9-_]+/g, "-").replace(/^-+|-+$/g, "") || "certificate";
};

export const getCertificateVerificationUrl = (certificate, companyDetails = {}) => {
  const existingLink = trimValue(certificate?.verificationLink);
  const identifier = trimValue(certificate?.identifier)
    || existingLink.split("/").filter(Boolean).pop();
  const verificationPath = identifier ? `/verify-certificate/${encodeURIComponent(identifier)}` : existingLink;

  if (!verificationPath) return "";
  if (/^https?:\/\//i.test(verificationPath)) return verificationPath;

  const companyUrl = trimValue(certificate?.company?.url)
    || trimValue(companyDetails?.url)
    || trimValue(companyDetails?.companyUrl);
  if (companyUrl) {
    return `${companyUrl.replace(/\/+$/, "")}${verificationPath.startsWith("/") ? verificationPath : `/${verificationPath}`}`;
  }

  if (typeof window === "undefined") return verificationPath;
  return `${window.location.origin}${verificationPath.startsWith("/") ? verificationPath : `/${verificationPath}`}`;
};

export const getCompanyLogoUrl = (companyDetails = {}, certificate = {}) => getTextValue(
  companyDetails?.logoImageContent?.displayLink,
  certificate?.company?.logoImageContent?.displayLink,
  companyDetails?.logoImageContent?.storedLink,
  certificate?.company?.logoImageContent?.storedLink,
  companyDetails?.logoLocation,
  certificate?.company?.logoLocation,
  companyDetails?.logoUrl,
  certificate?.company?.logoUrl,
  companyDetails?.logo,
  certificate?.company?.logo,
  typeof window !== "undefined" ? window.localStorage.getItem("companyLogo") : ""
);

const getCompanyLogoExportSource = (companyDetails = {}, certificate = {}) => {
  const source = getCompanyLogoUrl(companyDetails, certificate);
  if (!source || isDataUrl(source)) return source;
  return extractS3ObjectKey(source) || source;
};

const getSignatureUrl = (certificate = {}) => getTextValue(
  certificate?.signature?.displayLink,
  certificate?.signature?.storedLink,
  certificate?.templateData?.signatures?.[0]?.displayLink,
  certificate?.templateData?.signatures?.[0]?.storedLink
);

const getSignatureExportSource = (certificate = {}) => {
  const source = getSignatureUrl(certificate);
  if (!source || isDataUrl(source)) return source;
  return extractS3ObjectKey(source) || source;
};

export const findCourseCertificate = async ({ company, product, user }) => {
  const companyId = company?.primaryId;
  const productId = product?.primaryId;
  const userId = user?.primaryId;

  if (!companyId || !productId || !userId) return null;

  const certificates = await findCertificatesByCriteria({
    company: { primaryId: companyId },
    product: { primaryId: productId },
    user: { primaryId: userId }
  });

  return (Array.isArray(certificates) ? certificates : [])
    .filter((certificate) => !certificate?.deleted)
    .sort((left, right) => new Date(right?.issuedDate || right?.createdDate || 0) - new Date(left?.issuedDate || left?.createdDate || 0))[0] || null;
};

export const buildCourseCertificatePayload = ({ certificateGrade, company, course, completionDate, user }) => {
  const product = getCourseProduct(course);
  const companyName = getTextValue(company?.name, course?.company?.name, "AVPS Academy Inc.");
  const courseName = getTextValue(product?.name, product?.title, product?.displayName, "Course");
  const issuedAt = toDatePayload(completionDate);

  const grade = trimValue(certificateGrade);

  return {
    holderName: getUserName(user),
    completionDate: issuedAt,
    issuedDate: issuedAt,
    issuedBy: companyName,
    description: `This certificate confirms the successful completion of ${courseName}.`,
    status: "ACTIVE",
    createdBy:"Student",
    company: { primaryId: company?.primaryId },
    product: { primaryId: product?.primaryId },
    user: { primaryId: user?.primaryId },
    templateData: {
      headerDescription: "This certificate recognizes the successful completion of the course requirements.",
      accomplishmentText: "has successfully completed all required coursework and learning activities.",
      issuanceText: `Issued by ${companyName}${companyName.endsWith(".") ? "" : "."}`,
      footerDescription: "This certificate can be verified online using the certificate ID.",
      ...(grade ? { grade } : {})
    }
  };
};

export const generateCertificatePdfDocument = async (certificate, companyDetails = {}, dispatch = null) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.zIndex = "-1";
  container.style.pointerEvents = "none";
  document.body.appendChild(container);

  try {
    const verificationUrl = getCertificateVerificationUrl(certificate, companyDetails);
    const logoSource = getCompanyLogoExportSource(companyDetails, certificate);
    const logoDataUrl = logoSource ? await getLogoDataUrl(logoSource, dispatch) : "";
    const exportCompanyDetails = {
      ...(companyDetails || {}),
      ...(logoDataUrl ? { logoLocation: logoDataUrl, logoUrl: logoDataUrl } : {}),
      logoImageContent: {
        ...(companyDetails?.logoImageContent || {}),
        ...(logoDataUrl ? { displayLink: logoDataUrl, storedLink: logoDataUrl } : {})
      }
    };

    const signatureSource = getSignatureExportSource(certificate);
    const signatureDataUrl = signatureSource ? await getLogoDataUrl(signatureSource, dispatch) : "";
    const exportCertificate = signatureDataUrl ? {
      ...certificate,
      signature: {
        ...(certificate?.signature || {}),
        displayLink: signatureDataUrl,
        storedLink: signatureDataUrl
      },
      templateData: {
        ...(certificate?.templateData || {}),
        signatures: (certificate?.templateData?.signatures || []).map((signature, index) => (
          index === 0 ? { ...signature, displayLink: signatureDataUrl, storedLink: signatureDataUrl } : signature
        ))
      }
    } : certificate;

    ReactDOM.render(
      <CertificateDocument
        certificate={exportCertificate}
        companyDetails={exportCompanyDetails}
        verificationUrl={verificationUrl}
      />,
      container
    );
    await waitForNextPaint();

    const node = container.firstElementChild;
    if (!node) throw new Error("Unable to prepare certificate for export.");
    await waitForNodeImages(node);

    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      imageTimeout: 15000,
      windowWidth: node.scrollWidth
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
      compress: true
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imageRatio = canvas.width / canvas.height;
    let renderWidth = pageWidth;
    let renderHeight = renderWidth / imageRatio;

    if (renderHeight > pageHeight) {
      renderHeight = pageHeight;
      renderWidth = renderHeight * imageRatio;
    }

    const offsetX = (pageWidth - renderWidth) / 2;
    const offsetY = (pageHeight - renderHeight) / 2;

    pdf.addImage(
      imgData,
      "JPEG",
      offsetX,
      offsetY,
      renderWidth,
      renderHeight,
      undefined,
      "MEDIUM"
    );

    // The certificate is embedded as one flat raster image, so the "Verify Certificate"
    // text has no real hyperlink of its own in the PDF — overlay an invisible clickable
    // link annotation over that bottom text region (CertificateDocument's fixed
    // 1123x794 layout: the verification bar sits within the bottom ~40px, inset 80px
    // from each side) so the link is actually clickable in a PDF viewer.
    if (verificationUrl) {
      const DOC_WIDTH_PX = 1123;
      const DOC_HEIGHT_PX = 794;
      const scaleX = renderWidth / DOC_WIDTH_PX;
      const scaleY = renderHeight / DOC_HEIGHT_PX;
      pdf.link(
        offsetX + 80 * scaleX,
        offsetY + (DOC_HEIGHT_PX - 70) * scaleY,
        (DOC_WIDTH_PX - 160) * scaleX,
        40 * scaleY,
        { url: verificationUrl }
      );
    }

    return {
      fileName: `${getCertificateFileName(certificate)}.pdf`,
      pdf
    };
  } finally {
    ReactDOM.unmountComponentAtNode(container);
    container.remove();
  }
};

export const buildCertificatePdfAttachment = async (certificate, companyDetails = {}, dispatch = null) => {
  const { fileName, pdf } = await generateCertificatePdfDocument(certificate, companyDetails, dispatch);
  const pdfArrayBuffer = pdf.output("arraybuffer");

  return {
    fileName,
    contentType: "application/pdf",
    contentBase64: arrayBufferToBase64(pdfArrayBuffer)
  };
};

export const createCertificateNotificationEvent = ({ attachment, certificate, companyDetails }) => {
  const user = certificate?.user || {};
  const holderName = getTextValue(certificate?.holderName, getUserName(user));
  const firstName = getTextValue(user?.firstName, holderName.split(/\s+/).filter(Boolean)[0], holderName);
  const lastName = getTextValue(user?.lastName, holderName.split(/\s+/).filter(Boolean).slice(1).join(" "));
  const companyName = getTextValue(certificate?.company?.name, companyDetails?.name, "AVPS Academy Inc.");
  const courseName = getTextValue(certificate?.product?.name, certificate?.product?.title, certificate?.product?.displayName, "Course");
  const companyUrl = getTextValue(certificate?.company?.url, companyDetails?.url, companyDetails?.companyUrl);
  const email = trimValue(user?.email);

  return {
    type: "MANAGE_INVOICE",
    source: "KRUMAGO_MANAGEMENT_SERVICE",
    subType: "CREATED",
    eventDetailEntityType: "USER",
    notificationInfo: {
      notifiy: true,
      channels: ["EMAIL"],
      messageType: "MANAGE_CERTIFICATE"
    },
    attributes: [
      { value: getIntegerValue(certificate?.primaryId), key: "PRIMARY_ID" },
      { value: getTextValue(certificate?.identifier), key: "UNIQUE_IDENTIFIER" },
      { value: getTextValue(certificate?.identifier), key: "INVOICE_NUMBER" },
      { value: firstName, key: "FIRST_NAME" },
      { value: lastName, key: "LAST_NAME" },
      { value: holderName, key: "USER_NAME" },
      { value: courseName, key: "COURSE_NAME" },
      { value: companyName, key: "COMPANY_NAME" },
      { value: companyUrl, key: "COMPANY_URL" },
      { value: formatDateTime(certificate?.issuedDate, certificate?.company || companyDetails), key: "TRANSACTION_DATE" },
      { value: "CERTIFICATE_NOTIFICATION", key: "TRANSACTION_TYPE" },
      { value: email, key: "EMAIL_ID" },
      ...(attachment?.fileName && attachment?.contentType && attachment?.contentBase64
        ? [
          { value: attachment.fileName, key: "INFORMATION" },
          { value: attachment.contentType, key: "CONTENT_URL" },
          { value: attachment.contentBase64, key: "COMPANY_LOGO" }
        ]
        : [])
    ],
    users: [{
      primaryId: getIntegerValue(user?.primaryId),
      firstName,
      lastName,
      email,
      phoneNumber: trimValue(user?.phoneNumber),
      countryCode: trimValue(user?.countryCode || companyDetails?.contactPerson?.countryCode || companyDetails?.operatingLocation?.countryCode)
    }],
    company: {
      primaryId: getIntegerValue(certificate?.company?.primaryId, companyDetails?.primaryId),
      name: companyName,
      url: companyDetails?.url || companyDetails?.companyUrl || "",
      ...(certificate?.company?.domain?.type || companyDetails?.domain?.type
        ? { domain: { type: certificate?.company?.domain?.type || companyDetails?.domain?.type } }
        : {}),
      ...(getCompanyLogoUrl(companyDetails, certificate)
        ? { logoImageContent: { displayLink: getCompanyLogoUrl(companyDetails, certificate) } }
        : {})
    }
  };
};

export const sendCertificateEmail = async ({ certificate, companyDetails, dispatch }) => {
  const email = trimValue(certificate?.user?.email);
  if (!email) throw new Error("Learner email is missing.");

  const attachment = await buildCertificatePdfAttachment(certificate, companyDetails, dispatch);
  await dispatch(publishAvpsEvent(createCertificateNotificationEvent({
    attachment,
    certificate,
    companyDetails
  })));
};

const sendCertificateEmailOnce = ({ certificate, companyDetails, dispatch, requestKey }) => {
  if (!certificate || !dispatch) return Promise.resolve(null);

  if (!certificateNotificationRequests.has(requestKey)) {
    certificateNotificationRequests.set(
      requestKey,
      sendCertificateEmail({ certificate, companyDetails, dispatch })
    );
  }

  return certificateNotificationRequests.get(requestKey);
};

export const ensureCourseCertificate = async ({
  certificateGrade = "",
  company,
  completionDate,
  course,
  dispatch,
  evaluationResult = null,
  exams = null,
  sendNotification = true,
  user
}) => {
  const product = getCourseProduct(course);
  const requestKey = `${company?.primaryId || ""}:${product?.primaryId || ""}:${user?.primaryId || ""}`;

  if (!hasCertificateEnabled(course) || !company?.primaryId || !product?.primaryId || !user?.primaryId) {
    return { certificate: null, created: false };
  }

  if (certificateIssueRequests.has(requestKey)) {
    return certificateIssueRequests.get(requestKey);
  }

  const issueRequest = (async () => {
    const existingCertificate = await findCourseCertificate({ company, product, user });
    if (existingCertificate) {
      const certificate = {
        ...existingCertificate,
        company: existingCertificate?.company || company,
        product: existingCertificate?.product || product,
        user: existingCertificate?.user || user
      };
      // Certificate already exists — never re-send the email.
      // The notification fires only when the certificate is first created below.
      return { certificate, created: false };
    }

    const resolvedGrade = trimValue(certificateGrade) || resolveCertificateGrade({ evaluationResult, exams });
    const certificatePayload = buildCourseCertificatePayload({
      certificateGrade: resolvedGrade,
      company,
      course,
      completionDate,
      user
    });
    const issuedCertificate = await issueCertificate(certificatePayload);
    const certificate = {
      ...issuedCertificate,
      company: issuedCertificate?.company || company,
      product: issuedCertificate?.product || product,
      user: issuedCertificate?.user || user,
      createdBy: "student"
    };

    let notificationError = null;
    if (sendNotification) {
      try {
        await sendCertificateEmailOnce({ certificate, companyDetails: company, dispatch, requestKey });
      } catch (error) {
        notificationError = error;
      }
    }

    return { certificate, created: true, notificationError };
  })();

  certificateIssueRequests.set(requestKey, issueRequest);
  try {
    return await issueRequest;
  } catch (error) {
    certificateIssueRequests.delete(requestKey);
    throw error;
  }
};

export const downloadCertificate = async (certificate, companyDetails = {}, dispatch = null) => {
  const { fileName, pdf } = await generateCertificatePdfDocument(certificate, companyDetails, dispatch);
  pdf.save(fileName);
};

export const shareCertificate = async (certificate, companyDetails = {}) => {
  const url = getCertificateVerificationUrl(certificate, companyDetails);
  const title = `Certificate ${certificate?.identifier || ""}`.trim();

  if (navigator.share && url) {
    await navigator.share({ title, text: "View and verify my course completion certificate.", url });
    return;
  }

  if (navigator.clipboard && url) {
    await navigator.clipboard.writeText(url);
  }
};
